import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';
import { toast } from 'sonner';
import { SurveySubmissionSource } from '@shared/surveySubmissionSource';

import { useInviteClientTrigger, quickClientApi } from '@/features/invite-client';
import { selectSessionAdvisor, fetchClientsForAdvisorByAdvisorIdRequest } from '@client/model';
import { advisorOnboardingKeys } from '@client/api/waterlily';

export const selfTestIntakeFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  acknowledgement: z.literal(true, {
    errorMap: () => ({ message: 'You must acknowledge this statement to continue.' }),
  }),
});

export type SelfTestIntakeFormValues = z.infer<typeof selfTestIntakeFormSchema>;

export function useSelfTestIntakeForm() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const advisor = useSelector(selectSessionAdvisor);
  const { clearInviteClientParams } = useInviteClientTrigger();

  const form = useForm<SelfTestIntakeFormValues>({
    resolver: zodResolver(selfTestIntakeFormSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: advisor.advisorFirstName ?? '',
      lastName: advisor.advisorLastName ?? '',
      acknowledgement: undefined as unknown as true,
    },
  });

  const mutation = useMutation({
    mutationFn: quickClientApi.createQuickClientWithForm,
    onSuccess: data => {
      dispatch(
        fetchClientsForAdvisorByAdvisorIdRequest({
          advisorId: advisor.advisorId,
          isInvalidation: true,
        }),
      );
      queryClient.invalidateQueries({ queryKey: advisorOnboardingKeys.status(advisor.advisorId) });
      dispatch({ type: 'RESUME_SESSION_REFRESH' });
      clearInviteClientParams();
      const baseUrl = window.location.origin;
      const formUrl = `${baseUrl}/onboarding-form/${data.formReferenceId}?source=${SurveySubmissionSource.FILL_FORM}`;
      window.open(formUrl, '_blank', 'noopener,noreferrer');
      toast.success('Self-test plan created', {
        description: 'The intake form has been opened in a new tab.',
      });
    },
    onError: () => {
      toast.error('Failed to create self-test plan', {
        description: 'Please try again or contact support',
      });
    },
  });

  function onSubmit(data: SelfTestIntakeFormValues) {
    mutation.mutate({
      advisorId: advisor.advisorId,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      email: advisor.advisorEmail ?? '',
      acknowledgedEdjSelfPlanConsent: data.acknowledgement === true,
    });
  }

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isLoading: mutation.isPending,
  };
}
